import { create } from 'zustand'
import type { User } from '@/types'
import { API_BASE } from '@/lib/config'

interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  fetchUser: () => Promise<void>
  logout: () => void
}

export function getGoogleAuthUrl(): string {
  return `${API_BASE}/v1/auth/google/authorize`
}

// We intentionally do NOT persist auth state. The session is tied to the
// HttpOnly cookie set by the API; on every app start we verify it with
// /v1/auth/me. This keeps sign-out behaviour correct: clearing the cookie
// on the server invalidates the session immediately.
export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  loading: false,
  error: null,
  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  fetchUser: async () => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(`${API_BASE}/v1/auth/me`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) {
        if (res.status === 401) {
          set({ user: null, loading: false, error: null })
          return
        }
        throw new Error('Failed to load account')
      }
      const data = (await res.json()) as { success: boolean; customer: User }
      if (data.success) {
        set({ user: data.customer, loading: false, error: null })
      } else {
        set({ user: null, loading: false, error: data.success === false ? 'not_authenticated' : null })
      }
    } catch (err) {
      set({ user: null, loading: false, error: err instanceof Error ? err.message : 'unknown_error' })
    }
  },
  logout: () => {
    // Clear the server cookie first; then update local state.
    fetch(`${API_BASE}/v1/auth/logout`, { method: 'POST', credentials: 'include' })
      .catch(() => {})
      .finally(() => set({ user: null, error: null }))
  },
}))
